import { alpha } from '@mui/material/styles';

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// Setup Colors
const PRIMARY = {
  lighter: '#9bb9f7',
  light: '#5a8aea',
  main: '#055577',
  dark: '#0555C8',
  darker: '#05556C'
};
const SECONDARY = {
  lighter: '#89adf7',
  light: '#125bf1',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A'
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D'
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01'
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E'
};

const GREY = {
  0: '#FFFFFF',
  25: '#f6f7f8',
  50: 'rgba(0, 0, 0, 0.08)',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  701: '#333438',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

export const Palette = {
  // LIGHT
  light: {
    mode: 'light',
    primary: {
      25: '#F6FBFE',
      50: '#E2F4FD',
      restoreBlue: '#055577',
      quiet: '#042E43',
      doMore: '#308CBF',
      summit: '#F6FBFE',
      lighter: PRIMARY.lighter,
      light: PRIMARY.light,
      200: '#90d4f9',
      main: PRIMARY.main,
      600: '#1FA0E5',
      700: '#1991D2',
      800: '#1585C1',
      900: '#0D6DA0',
      dark: PRIMARY.dark,
      darker: PRIMARY.darker,
      contrastText: '#fff'
    },
    secondary: {
      lighter: SECONDARY.lighter,
      light: SECONDARY.light,
      main: SECONDARY.main,
      dark: SECONDARY.dark,
      darker: SECONDARY.darker,
      contrastText: '#fff'
    },
    info: {
      lighter: INFO.lighter,
      light: INFO.light,
      main: INFO.main,
      dark: INFO.dark,
      darker: INFO.darker,
      contrastText: '#fff'
    },
    success: {
      lighter: SUCCESS.lighter,
      light: SUCCESS.light,
      main: SUCCESS.main,
      400: '#26A666',
      dark: SUCCESS.dark,
      darker: SUCCESS.darker,
      contrastText: GREY[800]
    },
    warning: {
      lighter: WARNING.lighter,
      light: WARNING.light,
      main: WARNING.main,
      500: '#FA9200',
      dark: WARNING.dark,
      darker: WARNING.darker,
      contrastText: GREY[800]
    },
    error: {
      50: '#FFEBEE',
      lighter: ERROR.lighter,
      light: ERROR.light,
      main: ERROR.main,
      600: '#E53451',
      dark: ERROR.dark,
      darker: ERROR.darker,
      contrastText: '#fff'
    },
    grey: GREY,
    gradients: GRADIENTS,
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500]
    },
    divider: GREY[500_24],
    background: {
      paper: '#fff',
      default: '#fff',
      neutral: GREY[200]
    },
    action: {
      active: GREY[600],
      hover: GREY[500_8],
      selected: GREY[500_16],
      disabled: GREY[500_80],
      disabledBackground: GREY[500_24],
      focus: GREY[500_24],
      hoverOpacity: 0.08,
      disabledOpacity: 0.48
    }
  },
  retail3: {
    primary: {
      restoreBlue: '#055577',
      quiet: '#042E43',
      doMore: '#308CBF',
      summit: '#EEF6FA',
      25: '#F6FBFE',
      50: '#E2F4FD',
      100: '#BCE5FB',
      200: '#90D4F9',
      300: '#65C3F6',
      400: '#42B6F5',
      500: '#20A9F3',
      600: '#1FA0E5',
      700: '#1991D2',
      800: '#1585C1',
      900: '#0D6DA0',
      contrastText: '#fff'
    },
    secondary: {
      lighter: SECONDARY.lighter,
      light: SECONDARY.light,
      main: SECONDARY.main,
      dark: SECONDARY.dark,
      darker: SECONDARY.darker,
      contrastText: '#fff'
    },
    info: {
      lighter: INFO.lighter,
      light: INFO.light,
      main: INFO.main,
      dark: INFO.dark,
      darker: INFO.darker,
      contrastText: '#fff'
    },
    success: {
      25: '#F4FAF7',
      50: '#DFF1E8',
      100: '#B3DFC9',
      200: '#80CBA6',
      300: '#4EB782',
      400: '#26A666',
      500: '#00944A',
      600: '#008A45',
      700: '#007A3D',
      800: '#006B36',
      900: '#004D26',
      contrastText: GREY[800]
    },
    warning: {
      25: '#FFF9F0',
      50: '#FFF2E0',
      100: '#FFDFB2',
      200: '#FFCA80',
      300: '#FFB54D',
      400: '#FFA424',
      500: '#FF9500',
      600: '#FA9200',
      700: '#F58F00',
      800: '#F08C00',
      900: '#E58600',
      contrastText: GREY[800]
    },
    error: {
      25: '#FFF5F6',
      50: '#FFEBEE',
      100: '#FFCCD4',
      200: '#EF9AA8',
      300: '#E57184',
      400: '#EF526C',
      500: '#F43454',
      600: '#E53451',
      700: '#D3314C',
      701: '#b11a19',
      800: '#C72943',
      900: '#B51C36',
      contrastText: '#fff'
    },
    grey: {
      10: '#fafafa',
      25: '#F6F7F8',
      50: '#EDEFF2',
      100: '#D0D3DD',
      200: '#B0B5C4',
      300: '#8F96AE',
      400: '#78819B',
      500: '#606B8A',
      600: '#545D78',
      700: '#454D63',
      800: '#363C4E',
      900: '#252A37',
      contrastText: '#fff'
    },
    gradients: {
      primary: createGradient(
        'rgba(60, 100, 246, 0.08)',
        'rgba(60, 100, 246, 0.02)'
      ),
      secondary: createGradient(
        'rgba(107, 114, 128, 0.08)',
        'rgba(107, 114, 128, 0.02)'
      )
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)'
    },
    other: {
      [100]: '#0000001F',
      [200]: '#00000014',
      [300]: '#00000014',
      [400]: '#0000000F',
      [500]: '#FAFAFA'
    },
    divider: GREY[500_24],
    background: {
      paper: '#fff',
      default: '#fff',
      neutral: GREY[200]
    },
    action: {
      active: GREY[600],
      hover: GREY[500_8],
      selected: GREY[500_16],
      disabled: GREY[500_80],
      disabledBackground: GREY[500_24],
      focus: GREY[500_24],
      hoverOpacity: 0.08,
      disabledOpacity: 0.48
    },
    backdrop: 'rgba(246, 251, 254, 0.6)'
  }
};

export default Palette;
